import './gigiList.css'

import { Column } from 'primereact/column'
import { DataTable } from 'primereact/datatable'
import { useEffect, useMemo } from 'react'

import { PageHeader, ScenarioBadge, Spinner } from '../../../Components'
import { useAuth } from '../../../Provider/AuthProvider'
import { getDateString } from '../../../Services/Functions'
import { prefetchQuery, useCInfiniteQuery, useCQuery } from '../../../Services/QueryCache'
import { cap } from '../../../State'
import { useCapsule } from '../../../State/Functions'
import { translations } from '../../../translations'
import {
  ActiveFilterChips,
  ConversationActions,
  FilterBar,
  StatusBadge,
  TravellerCell
} from './Components'
import {
  AGENT_STATE_FILTERS,
  getChannelIcon,
  getExecutionState,
  hasOperatorWarning
} from './Functions'

const language = cap.language.state || 'IT'
const t = translations[language] || translations.IT

/**
 * Pagina lista conversazioni per gli operatori.
 * Mostra le conversazioni in una tabella con filtri inline, badge stato,
 * ricerca globale e navigazione al dettaglio.
 * Aggiornamento real-time via socket senza blocco dell'interfaccia.
 * Filtri persistenti alla navigazione (dettaglio -> lista) tramite capsule.
 * @returns {JSX.Element} Componente pagina lista conversazioni
 */
export function GigiList() {
  const {
    user: { user: { isola: defaultIsola = '', role: userRole = '', environment } = {} }
  } = useAuth()
  const isOperator = userRole === 'operator'
  const isOperatorGoelett = isOperator && environment?.codSociety === 'GOE'

  // Filtri persistenti tramite capsule (sopravvivono alla navigazione)
  const [selectedDate, setSelectedDate] = useCapsule(cap.selectedDateGigiList)
  const [selectedIsola, setSelectedIsola] = useCapsule(cap.gigiListIsola)
  const [filters, setFilters] = useCapsule(cap.gigiListFilters)

  // Inizializzazione isola: se il capsule è vuoto, usa il default dell'utente
  useEffect(() => {
    if (!selectedIsola && defaultIsola) {
      setSelectedIsola(defaultIsola)
    }
  }, [defaultIsola])

  // Operatore: filtro stato "in attesa di conferma" come default (escluso per GOE)
  useEffect(() => {
    if (isOperator && !isOperatorGoelett && !filters.state) {
      setFilters({ ...filters, state: 'IN_ATTESA' })
    }
  }, [])

  // Query dati conversazioni e isole
  const {
    data: conversations = [],
    isSuccess,
    isFetchingNextPage,
    refetch
  } = useCInfiniteQuery(['conversations', selectedDate, selectedIsola])
  const { data: isole = [], isSuccess: isSuccessIsole } = useCQuery(['isoleConversations'])
  const { data: { attivo: agenticoAttivo = false } = {}, isSuccess: isSuccessAgentico } = useCQuery(
    ['agenticoAttivo']
  )

  // Un filtro agentico rimasto nei filtri salvati, senza più canali agentici, nasconderebbe
  // tutte le righe con un'opzione che il dropdown non mostra: si toglie.
  useEffect(() => {
    if (isSuccessAgentico && !agenticoAttivo && filters.state in AGENT_STATE_FILTERS) {
      setFilters({ ...filters, state: '' })
    }
  }, [isSuccessAgentico, agenticoAttivo])

  // Refetch lista al mount (es. ritorno da dettaglio) per allineare con eventuali aggiornamenti socket
  useEffect(() => {
    refetch()
  }, [refetch])

  // Indicatore di caricamento (NON bloccante)
  const isLoading = !isSuccess || !isSuccessIsole

  // Prepara i dati delle conversazioni con campi calcolati
  const conversationsTable = useMemo(() => {
    if (!isSuccess) return []
    return conversations.map((r) => {
      // Sul ramo agentico l'attenzione richiesta non si deduce dai processi.
      const hasWarning = hasOperatorWarning(r)
      return {
        ...r,
        travellerSearch: [
          r.traveller?.pid,
          r.traveller?.email,
          r.traveller?.phone,
          r.traveller?.name + ' ' + r.traveller?.surname
        ]
          ?.filter((e) => e)
          .join(' - '),
        createdAt: new Date(r.createdAt).toLocaleString(),
        createdAtTimestamp: new Date(r.createdAt).getTime(),
        processState: getExecutionState(r),
        hasWarning
      }
    })
  }, [conversations, isSuccess])

  // Applica tutti i filtri e ordina: warning (in attesa) sempre in cima
  const filteredConversations = useMemo(() => {
    if (!conversationsTable.length) return []

    const filtered = conversationsTable.filter((conv) => {
      // Filtro ricerca globale
      const searchMatch =
        !filters.searchQuery ||
        conv.travellerSearch?.toLowerCase().includes(filters.searchQuery.toLowerCase()) ||
        (conv.conversationIdMidOffice &&
          conv.conversationIdMidOffice.toString().includes(filters.searchQuery)) ||
        (conv.processState?.label &&
          conv.processState.label.toLowerCase().includes(filters.searchQuery.toLowerCase())) ||
        (conv.isola && conv.isola.toLowerCase().includes(filters.searchQuery.toLowerCase()))

      // Filtro stato (dropdown)
      const stateMatch = !filters.state || matchStateFilter(conv, filters.state)

      // Filtro AIC (non usato nella FilterBar attuale, ma mantenuto per compatibilità)
      const aicMatch =
        !filters.aic ||
        (conv.conversationIdMidOffice &&
          conv.conversationIdMidOffice.toString().includes(filters.aic))

      // Filtro richiedente
      const travellerMatch =
        !filters.traveller ||
        conv.travellerSearch?.toLowerCase().includes(filters.traveller.toLowerCase())

      return searchMatch && stateMatch && aicMatch && travellerMatch
    })

    if (isOperator) {
      return filtered.sort((a, b) => a.createdAtTimestamp - b.createdAtTimestamp)
    }

    // Altri utenti: conversazioni con warning (in attesa di conferma) sempre in cima
    return filtered.sort((a, b) => {
      if (a.hasWarning && !b.hasWarning) return -1
      if (!a.hasWarning && b.hasWarning) return 1
      return 0
    })
  }, [conversationsTable, filters, isOperator])

  /**
   * Gestisce il cambio di un singolo filtro
   * @param {string} filterName - Nome del filtro da aggiornare
   * @param {string} value - Nuovo valore del filtro
   */
  const handleFilterChange = (filterName, value) => {
    setFilters({ ...filters, [filterName]: value })
  }

  /**
   * Resetta tutti i filtri ai valori di default
   */
  const handleResetFilters = () => {
    setFilters({ traveller: '', aic: '', state: '', searchQuery: '' })
    setSelectedIsola(defaultIsola || '')
    setSelectedDate(getDateString(new Date()))
  }

  /**
   * Rimuove un singolo filtro specifico
   * @param {string} filterName - Nome del filtro da rimuovere
   */
  const handleFilterClear = (filterName) => {
    setFilters({ ...filters, [filterName]: '' })
  }

  /**
   * Resetta la data alla data corrente
   */
  const handleDateClear = () => {
    setSelectedDate(getDateString(new Date()))
  }

  /**
   * Prefetch dei dati quando il mouse passa su una riga
   * @param {Object} e - Evento mouse enter sulla riga
   */
  const handleRowMouseEnter = (e) => {
    if (e.data?.id) {
      prefetchQuery(['conversation', e.data.id.toString()])
    }
  }

  /**
   * Template per la colonna canale di comunicazione
   * @param {Object} rowData - Dati della riga
   * @returns {JSX.Element} Icona del canale
   */
  const channelTemplate = (rowData) => (
    <div className="gigi-channel-icon" title={rowData.communicationChannel || ''}>
      <i className={getChannelIcon(rowData.communicationChannel)} />
    </div>
  )

  /**
   * Template per la colonna stato con StatusBadge
   * @param {Object} rowData - Dati della riga (conv con processState, status, noraIsIncluded)
   * @returns {JSX.Element} Badge stato colorata
   */
  const stateTemplate = (rowData) => (
    <>
      <StatusBadge processState={rowData.processState} status={rowData.status} conv={rowData} />
      {/* Accanto allo stato, non al posto suo: sono due domande diverse
          («a che punto è?» e «su quale scenario gira?»). Assente senza profilo. */}
      <ScenarioBadge conv={rowData} />
    </>
  )

  /**
   * Template per la colonna richiedente
   * @param {Object} rowData - Dati della riga
   * @returns {JSX.Element} Cella strutturata del richiedente
   */
  const travellerTemplate = (rowData) => <TravellerCell traveller={rowData.traveller} />

  /**
   * Template per la colonna azioni
   * @param {Object} rowData - Dati della riga
   * @returns {JSX.Element} Bottoni azione
   */
  const actionsTemplate = (rowData) => (
    <ConversationActions id={rowData.id} hasWarning={rowData.hasWarning} />
  )

  return (
    <div className="gigi-list-container">
      <PageHeader
        title={t.conversations}
        actions={
          (isFetchingNextPage || isLoading) && (
            <div className="gigi-list-loading">
              <Spinner style={{ width: 16, height: 16, marginTop: 0 }} />
              <span>{t.caricamento}</span>
            </div>
          )
        }
      />

      <ActiveFilterChips
        selectedDate={selectedDate}
        onDateClear={handleDateClear}
        selectedIsola={selectedIsola}
        onIsolaClear={() => setSelectedIsola(defaultIsola || '')}
        defaultIsola={defaultIsola}
        isole={isole}
        filters={filters}
        onFilterClear={handleFilterClear}
        onResetAll={handleResetFilters}
        filteredCount={filteredConversations.length}
        totalCount={conversationsTable.length}
      />

      <FilterBar
        selectedDate={selectedDate}
        onDateChange={setSelectedDate}
        selectedIsola={selectedIsola}
        onIsolaChange={setSelectedIsola}
        defaultIsola={defaultIsola}
        isole={isole}
        filters={filters}
        onFilterChange={handleFilterChange}
        onReset={handleResetFilters}
        isLoading={isLoading}
        agenticoAttivo={agenticoAttivo}
      />

      <div className="ncard gigi-list-table-wrap" style={{ padding: 0, marginTop: 16 }}>
        <div className="ncard__head">
          <div className="ncard__ico">
            <i className="pi pi-list" style={{ fontSize: 16 }} />
          </div>
          <div>
            <h3 className="ncard__title">{t.conversations}</h3>
            <div className="ncard__sub">
              {filteredConversations.length} {t.gfDi} {conversationsTable.length} {t.conversazioni}
            </div>
          </div>
        </div>
        <DataTable
          style={{ width: '100%' }}
          className="TableSignatures"
          value={filteredConversations}
          emptyMessage={t.noConversationsFound}
          responsiveLayout="scroll"
          paginator
          rows={10}
          onRowMouseEnter={handleRowMouseEnter}
        >
          <Column
            header=""
            body={channelTemplate}
            style={{ textAlign: 'center', border: 'none', width: 60 }}
          />
          {!selectedIsola && (
            <Column
              field="isola"
              header={t.currentIsola}
              sortable
              style={{ textAlign: 'left', border: 'none', width: 100 }}
              body={(rowData) =>
                rowData.isola ? <span className="gigi-isola-badge">{rowData.isola}</span> : null
              }
            />
          )}
          <Column
            field="travellerSearch"
            header={t.requester}
            sortable
            style={{ border: 'none' }}
            body={travellerTemplate}
          />
          <Column
            field="conversationIdMidOffice"
            header={t.aic}
            sortable
            style={{ textAlign: 'left', border: 'none', width: 90 }}
            body={(rowData) => (
              <span className="gigi-aic-num">#{rowData.conversationIdMidOffice}</span>
            )}
          />
          <Column
            field="processState.label"
            header={t.state}
            sortable
            style={{ textAlign: 'left', border: 'none', width: 280 }}
            body={stateTemplate}
          />
          <Column
            field="createdAtTimestamp"
            header={t.creationDate}
            sortable
            style={{ textAlign: 'left', border: 'none', width: 180 }}
            body={(rowData) => <span className="gigi-date-meta">{rowData.createdAt}</span>}
          />
          <Column
            header={<span className="sr-only">{t.azioni}</span>}
            style={{ border: 'none', width: 90, textAlign: 'center' }}
            body={actionsTemplate}
          />
        </DataTable>
      </div>
    </div>
  )
}

/**
 * Verifica se una conversazione corrisponde al filtro stato selezionato.
 * Gestisce gli stati user-facing derivati dalla logica di getProcessState.
 * @param {Object} conv - Oggetto conversazione con processState, status, numeroTrasferta, noraIsIncluded
 * @param {string} stateCode - Codice stato user-facing selezionato nel filtro
 * @returns {boolean} true se la conversazione corrisponde al filtro
 */
function matchStateFilter(conv, stateCode) {
  if (!stateCode) return true

  // I due insiemi di codici non si sovrappongono, e la separazione è voluta.
  // Un codice legacy come `IN_ESECUZIONE` finisce nel `default: return true`, quindi
  // senza questa guardia selezionerebbe **tutte** le conversazioni agentiche; e un codice
  // agentico su una conversazione legacy farebbe lo stesso al contrario.
  const eAgentica = conv?.execution?.engine === 'agent'
  const codiceAgentico = stateCode in AGENT_STATE_FILTERS
  if (codiceAgentico) {
    return eAgentica && conv.execution.status === AGENT_STATE_FILTERS[stateCode]
  }
  if (eAgentica) {
    // Gli unici codici legacy che restano sensati su una conversazione agentica sono
    // quelli che parlano dello stato della conversazione, non dei processi.
    if (stateCode === 'CANCELLATA') return conv.status === 'canceled'
    if (stateCode === 'IGNORATA') return conv.status === 'ignored'
    if (stateCode === 'IN_ATTESA') return conv.execution.status === 'waiting_operator'
    return false
  }

  switch (stateCode) {
    case 'IN_ATTESA':
      return conv.status === 'paused'
    case 'CANCELLATA':
      return conv.status === 'canceled'
    case 'IGNORATA':
      return conv.status === 'ignored'
    case 'TRASFERTA':
      return conv.status === 'ended' && !!conv.numeroTrasferta
    case 'TERMINATO':
      return conv.processState?.value === 3 && conv.status === 'ended' && !conv.numeroTrasferta
    case 'NORA_ESCLUSA':
      return conv.processState?.value === 3 && conv.status === 'error' && !conv.noraIsIncluded
    case 'GESTIRE_MIDOFFICE':
      return conv.processState?.value === 3 && conv.status === 'error' && !!conv.noraIsIncluded
    case 'IN_ESECUZIONE': {
      // Tutto ciò che non rientra negli stati speciali (fallback di getProcessState)
      if (['paused', 'canceled', 'ignored'].includes(conv.status)) return false
      if (conv.processState?.value === 3) return false
      return true
    }
    default:
      return true
  }
}
